import React, { useMemo } from "react";
import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Button,
  Box,
  Heading,
  Icon,
  Text,
  Tabs,
  TabList,
  Tab,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import useAxios from "axios-hooks";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { FaChargingStation, FaBolt, FaMoneyBillWave } from "react-icons/fa";
import { MdTimer } from "react-icons/md";

import { toastState } from "atoms/toastState";
import { activeConnectorState } from "atoms/activeConnectorState";
import logger from "infra/logger";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";

const MODE_OPTIONS = [
  { value: "money", label: "Money", icon: FaMoneyBillWave, unit: "Rupees" },
  { value: "units", label: "Units", icon: FaBolt, unit: "Units" },
  { value: "time", label: "Time", icon: MdTimer, unit: "Minutes" },
];

const GlassMorphismBox = styled(Box)`
  backdrop-filter: blur(16px);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  padding: 0.75rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(245, 125, 54, 0.25);

  @media (min-width: 768px) {
    padding: 1rem;
  }

  &:hover {
    border-color: rgba(245, 125, 54, 0.4);
  }
`;

const GlassMorphismInput = styled(Input)`
  backdrop-filter: blur(6px);
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.3s ease;
  border-radius: 10px;
  height: 34px;
  border: 1px solid rgba(245, 125, 54, 0.25);

  @media (min-width: 768px) {
    height: 36px;
  }

  &:hover,
  &:focus {
    border-color: #f57d36;
    box-shadow: 0 4px 20px rgba(245, 125, 54, 0.25);
  }
`;

const StyledTab = styled(Tab)`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.75rem;
  color: #718096;
  padding: 0.3rem 0.6rem;
  transition: all 0.2s ease;
  border: 1px solid rgba(245, 125, 54, 0.2);
  background: rgba(255, 255, 255, 0.5);

  @media (min-width: 768px) {
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
  }

  &[aria-selected="true"] {
    background: rgba(245, 125, 54, 0.12);
    color: #f57d36;
    font-weight: 600;
    border-color: rgba(245, 125, 54, 0.4);
    box-shadow: 0 2px 6px rgba(245, 125, 54, 0.12);
  }

  &:hover:not([aria-selected="true"]) {
    background: rgba(245, 125, 54, 0.06);
    color: #f57d36;
    border-color: rgba(245, 125, 54, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StartCharging = ({ refresh, connector: connectorProp }) => {
  const activeConnectorFromState = useRecoilValue(activeConnectorState);
  const activeConnector = connectorProp ?? activeConnectorFromState;
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const canEdit = accessLevels[role]?.accessLevel <= 2;
  const setToast = useSetRecoilState(toastState);
  const [{ data, loading, error }, execute] = useAxios(
    { method: "POST", url: `/api/ocpp16/charging/start` },
    { manual: true }
  );

  const validationSchema = yup.object().shape({
    mode: yup.string().required("Mode is Required"),
    value: yup.number().moreThan(0).required(""),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      mode: "money",
      value: 0,
    },
  });

  const selectedMode = watch("mode");
  const activeMode = useMemo(
    () => MODE_OPTIONS.find((m) => m.value === selectedMode) || MODE_OPTIONS[0],
    [selectedMode]
  );

  const labelColor = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("#f57d36", "#ff9966");
  const isStartDisabled =
    activeConnector?.Status !== "Preparing" &&
    activeConnector?.Status !== "Available";

  const startCharging = async () => {
    const formValues = getValues();
    const body = {
      EvseSerialId: activeConnector.EvseSerialId,
      StationId: activeConnector.EvseId,
      IdTag: `${formValues.mobileNumber}`,
      Mode: formValues.mode,
      Value: parseFloat(formValues.value),
    };

    try {
      const response = await execute({ data: body });
      setToast({
        isOpen: true,
        title: "Charging Started Successfully",
        description: response.data.data.Message,
        status: "success",
      });
    } catch (error) {
      const err = error?.response?.data?.error;
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error occured when starting Charger",
        description: err ? err : null,
        status: "error",
      });
    }
    refresh();
  };

  return (
    <GlassMorphismBox
      width="100%"
      height="100%"
      position="relative"
      overflow="hidden"
    >
      <Flex mb={{ base: 2.5, md: 3 }} align="center">
        <Icon
          as={FaChargingStation}
          color="#f57d36"
          boxSize={{ base: 4, md: 4 }}
          mr={2}
        />
        <Heading
          size="sm"
          fontWeight={600}
          color={labelColor}
          fontSize={{ base: "sm", md: "sm" }}
        >
          Configure Charging Session
        </Heading>
      </Flex>

      <VStack spacing={{ base: 3, md: 3 }} align="stretch">
        {/* Mode Tabs */}
        <Controller
          name="mode"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={!!errors.mode}>
              <FormLabel
                fontSize="xs"
                fontWeight={600}
                color={labelColor}
                mb={1.5}
                display="flex"
                alignItems="center"
              >
                <Icon as={FaBolt} color={iconColor} mr={1.5} boxSize={3} />
                Select Mode
              </FormLabel>
              <Tabs
                index={MODE_OPTIONS.findIndex((m) => m.value === field.value)}
                onChange={(index) => field.onChange(MODE_OPTIONS[index].value)}
                variant="unstyled"
              >
                <TabList gap={{ base: 1.5, md: 2 }} flexWrap="wrap">
                  {MODE_OPTIONS.map((option) => (
                    <StyledTab
                      key={option.value}
                      isDisabled={!canEdit}
                    >
                      <Icon as={option.icon} boxSize={3} />
                      <Text>{option.label}</Text>
                    </StyledTab>
                  ))}
                </TabList>
              </Tabs>
              <FormErrorMessage fontSize={{ base: "2xs", md: "xs" }}>
                {errors.mode?.message}
              </FormErrorMessage>
            </FormControl>
          )}
        />

        {/* Value + Button Row */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          gap={{ base: 2, md: 2.5 }}
          align={{ base: "stretch", sm: "flex-end" }}
        >
          <Controller
            name="value"
            control={control}
            render={({ field }) => (
              <FormControl
                isInvalid={!!errors.value}
                flex="1"
                maxW={{ base: "100%", sm: "220px", md: "260px" }}
              >
                <FormLabel
                  fontSize="xs"
                  fontWeight={600}
                  color={labelColor}
                  mb={1.5}
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={FaMoneyBillWave} color={iconColor} mr={1.5} boxSize={3} />
                  Value
                </FormLabel>
                <GlassMorphismInput
                  {...field}
                  required
                  isDisabled={!canEdit}
                  placeholder={`Enter ${activeMode.unit}`}
                  type="number"
                  min={0}
                  fontSize="sm"
                  _focus={{
                    borderColor: "#f57d36",
                    boxShadow: "0 0 0 1px #f57d36",
                  }}
                />
                <FormErrorMessage fontSize="2xs">
                  {errors.value && "value must be greater than 0"}
                </FormErrorMessage>
              </FormControl>
            )}
          />

          <Button
            isDisabled={isStartDisabled}
            bg="linear-gradient(90deg, #f57d36 0%, #ff9966 100%)"
            color="white"
            onClick={handleSubmit(startCharging)}
            borderRadius="md"
            leftIcon={<FaChargingStation size={14} />}
            isLoading={loading}
            _hover={{
              transform: "translateY(-1px)",
              boxShadow: "0 6px 14px rgba(245, 125, 54, 0.3)",
            }}
            _active={{
              transform: "translateY(0)",
              boxShadow: "0 3px 6px rgba(245, 125, 54, 0.3)",
            }}
            _disabled={{
              opacity: 0.6,
              cursor: "not-allowed",
              transform: "none",
            }}
            transition="all 0.3s ease"
            fontWeight={600}
            fontSize="sm"
            w={{ base: "100%", sm: "auto" }}
            minW={{ sm: "130px" }}
            h="36px"
          >
            Start Charging
          </Button>
        </Flex>
      </VStack>

      {isStartDisabled && (
        <Text fontSize="xs" color="gray.500" mt={2} textAlign="right">
          Connector is not available for charging
        </Text>
      )}
    </GlassMorphismBox>
  );
};

export default StartCharging;
